import { makeObservable, observable, action, computed, reaction } from 'mobx';
import {
    startOfWeek,
    startOfDay,
    endOfDay,
    addDays,
    subDays,
    addWeeks,
    subWeeks,
    addMonths,
    subMonths,
    addQuarters,
    subQuarters,
    startOfMonth,
    startOfQuarter,
    startOfISOWeek,
    format,
    differenceInDays,
    isBefore,
    isAfter
} from 'date-fns';

class GanttStore {
    tasks: any[] = [];
    isLoading = false;
    error = null;
    viewMode = 'week'; // 'day', 'week', 'month', 'quarter'
    visibleStartDate = startOfWeek(new Date()); // The leftmost date visible
    selectedTaskId = null;

    constructor() {
        // Use makeObservable to explicitly define observables, actions, and computed properties
        makeObservable(this, {
            tasks: observable,
            isLoading: observable,
            error: observable,
            viewMode: observable,
            visibleStartDate: observable,
            selectedTaskId: observable,
            setTasks: action,
            setLoading: action,
            setError: action,
            setViewMode: action,
            setVisibleStartDate: action,
            setSelectedTaskId: action,
            fetchTasks: action,
            updateTask: action,
            addTask: action,
            deleteTask: action,
            timeColumns: computed,
            getColumnWidth: computed, // Mark computed even if it's a method returning a simple value
            getVisibleDuration: computed, // Mark computed
        });

        // Example reaction: Persist view settings if needed
        reaction(
            () => this.viewMode,
            (viewMode: any) => {
                console.log(`Gantt view mode changed to: ${viewMode}`);
                // Potentially save to localStorage or user settings via API
            }
        );

        // Initial data fetch
        this.fetchTasks();
    }

    setTasks(tasks: any) {
        this.tasks = tasks;
    }

    setLoading(loading: any) {
        this.isLoading = loading;
    }

    setError(error: any) {
        this.error = error;
    }

    setViewMode(mode: any) {
        this.viewMode = mode;
        // Adjust visibleStartDate based on new viewMode to keep context
        this.adjustVisibleStartDateForViewMode();
    }

    setVisibleStartDate(date: any) {
        this.visibleStartDate = new Date(date);
    }

    setSelectedTaskId(taskId: any) {
        this.selectedTaskId = taskId;
    }

    // --- Data Fetching (integrate with FeathersJS client) ---
    async fetchTasks() {
        this.setLoading(true);
        this.setError(null);
        try {
            // Replace with your FeathersJS client call
            // const response = await feathersClient.service('gantt-tasks').find();
            // this.setTasks(response.data); // Assuming response.data is an array of tasks

            // Mock data for development
            const mockTasks = [
                {
                    id: 'task-1',
                    name: 'Design Wall Panel WP-001',
                    type: 'Engineering',
                    productId: 'WP-001',
                    startDate: startOfDay(subDays(new Date(), 5)).toISOString(),
                    endDate: endOfDay(subDays(new Date(), 3)).toISOString(),
                    progress: 100,
                    dependencies: [],
                    resources: ['Eng-Team'],
                    status: 'Completed'
                },
                {
                    id: 'task-2',
                    name: 'Mold Setup for WP-001',
                    type: 'Mold Setup',
                    productId: 'WP-001',
                    startDate: startOfDay(subDays(new Date(), 2)).toISOString(),
                    endDate: endOfDay(subDays(new Date(), 1)).toISOString(),
                    progress: 100,
                    dependencies: ['task-1'],
                    resources: ['Mold-Crew A'],
                    status: 'Completed'
                },
                {
                    id: 'task-3',
                    name: 'Pouring WP-001',
                    type: 'Pouring',
                    productId: 'WP-001',
                    startDate: startOfDay(new Date()).toISOString(),
                    endDate: endOfDay(addDays(new Date(), 1)).toISOString(),
                    progress: 50,
                    dependencies: ['task-2'],
                    resources: ['Prod-Crew B', 'Crane 1'],
                    status: 'In Progress'
                },
                {
                    id: 'task-4',
                    name: 'Curing Slab SL-005',
                    type: 'Curing',
                    productId: 'SL-005',
                    startDate: startOfDay(addDays(new Date(), 1)).toISOString(),
                    endDate: endOfDay(addDays(new Date(), 3)).toISOString(),
                    progress: 0,
                    dependencies: [],
                    resources: ['Curing-Area 1'],
                    status: 'Planned'
                },
                {
                    id: 'task-5',
                    name: 'QC Check WP-001',
                    type: 'Quality Control',
                    productId: 'WP-001',
                    startDate: startOfDay(addDays(new Date(), 2)).toISOString(),
                    endDate: endOfDay(addDays(new Date(), 2)).toISOString(),
                    progress: 0,
                    dependencies: ['task-3'], // Depends on pouring completion
                    resources: ['QC-Team'],
                    status: 'Planned'
                },
                {
                    id: 'task-6',
                    name: 'Stripping WP-001',
                    type: 'Stripping',
                    productId: 'WP-001',
                    startDate: startOfDay(addDays(new Date(), 3)).toISOString(),
                    endDate: endOfDay(addDays(new Date(), 3)).toISOString(),
                    progress: 0,
                    dependencies: ['task-5'], // Depends on QC
                    resources: ['Mold-Crew A'],
                    status: 'Planned'
                },
                {
                    id: 'task-7',
                    name: 'Storage WP-001',
                    type: 'Storage',
                    productId: 'WP-001',
                    startDate: startOfDay(addDays(new Date(), 4)).toISOString(),
                    endDate: endOfDay(addDays(new Date(), 4)).toISOString(),
                    progress: 0,
                    dependencies: ['task-6'],
                    resources: ['Yard Crew'],
                    status: 'Planned'
                },
            ];
            this.setTasks(mockTasks);

        } catch (err) {
            console.error('Failed to fetch tasks:', err);
            this.setError(err);
        } finally {
            this.setLoading(false);
        }
    }

    // --- Actions for Task Manipulation (will call FeathersJS APIs) ---
    async updateTask(taskId: any, updates: any) {
        // Optimistic update
        const taskIndex = this.tasks.findIndex(t => t.id === taskId);
        if (taskIndex > -1) {
            const originalTask = { ...this.tasks[taskIndex] };
            this.tasks[taskIndex] = { ...this.tasks[taskIndex], ...updates };
            try {
                // Replace with FeathersJS client call
                // await feathersClient.service('gantt-tasks').patch(taskId, updates);
                console.log(`Task ${taskId} updated successfully (mock)`);
            } catch (err) {
                console.error(`Failed to update task ${taskId}:`, err);
                this.setError(err);
                // Rollback on error
                this.tasks[taskIndex] = originalTask;
            }
        }
    }

    async addTask(task: any) {
        // Generate a temporary ID for optimistic update
        const tempId = `temp-${Date.now()}`;
        const newTask = { id: tempId, ...task, status: task.status || 'Planned' };
        this.tasks.push(newTask);
        try {
            // Replace with FeathersJS client call, use specific service for precast tasks
            // const createdTask = await feathersClient.service('precast-tasks').create(task);
            // Replace temporary ID with actual ID from backend
            const actualId = `task-${Date.now()}`; // Mock actual ID
            this.tasks = this.tasks.map(t => t.id === tempId ? { ...newTask, id: actualId } : t); // Use newTask here, createdTask from API
            console.log(`Task added successfully (mock): ${actualId}`);
        } catch (err) {
            console.error('Failed to add task:', err);
            this.setError(err);
            this.tasks = this.tasks.filter(t => t.id !== tempId); // Remove on error
        }
    }

    async deleteTask(taskId: any) {
        const originalTasks = [...this.tasks];
        this.tasks = this.tasks.filter(t => t.id !== taskId);
        try {
            // Replace with FeathersJS client call
            // await feathersClient.service('gantt-tasks').remove(taskId);
            console.log(`Task ${taskId} deleted successfully (mock)`);
        } catch (err) {
            console.error(`Failed to delete task ${taskId}:`, err);
            this.setError(err);
            this.tasks = originalTasks; // Rollback
        }
    }

    // --- Helper Methods & Computed Values ---

    // Calculate columns for the Gantt chart based on view mode and visible dates
    get timeColumns() {
        const columns = [];
        let current = this.viewMode === 'day'
            ? startOfDay(this.visibleStartDate)
            : startOfWeek(this.visibleStartDate); // Start of week for week/month/quarter view
        const end = addDays(this.visibleStartDate, this.getVisibleDuration); // Use computed property

        while (isBefore(current, end)) {
            const columnDate = new Date(current);
            let headerFormat = '';
            let unit = '';
            const nextIncrement = 1;

            switch (this.viewMode) {
                case 'day':
                    headerFormat = 'eee d MMM';
                    unit = 'days';
                    break;
                case 'week':
                    headerFormat = 'd MMM'; // Start date of the week
                    unit = 'weeks';
                    break;
                case 'month':
                    headerFormat = 'MMM yyyy';
                    unit = 'months';
                    break;
                case 'quarter':
                    headerFormat = 'QQQ yyyy';
                    unit = 'quarters';
                    break;
                default:
                    headerFormat = 'eee d MMM';
                    unit = 'days';
            }
            columns.push({
                date: columnDate,
                label: format(columnDate, headerFormat),
                width: this.getColumnWidth, // Use computed property
                unit: unit // Unit for iteration
            });

            if (unit === 'days') {
                current = addDays(current, nextIncrement);
            } else if (unit === 'weeks') {
                current = addWeeks(current, nextIncrement);
            } else if (unit === 'months') {
                current = addMonths(current, nextIncrement);
            } else if (unit === 'quarters') {
                current = addQuarters(current, nextIncrement);
            }
        }
        return columns;
    }

    // Define the width of a single time column based on view mode
    get getColumnWidth() {
        switch (this.viewMode) {
            case 'day': return 100; // Pixels per day
            case 'week': return 250; // Pixels per week
            case 'month': return 350; // Pixels per month
            case 'quarter': return 450; // Pixels per quarter
            default: return 100;
        }
    }

    // Adjust visibleStartDate to align with the start of the current view mode unit
    adjustVisibleStartDateForViewMode() {
        let newStartDate = new Date(this.visibleStartDate);
        switch (this.viewMode) {
            case 'day':
                newStartDate = startOfDay(newStartDate);
                break;
            case 'week':
                newStartDate = startOfISOWeek(newStartDate); // Use isoWeek for Monday start
                break;
            case 'month':
                newStartDate = startOfMonth(newStartDate);
                break;
            case 'quarter':
                newStartDate = startOfQuarter(newStartDate);
                break;
            default:
                newStartDate = startOfDay(newStartDate);
        }
        this.visibleStartDate = newStartDate;
    }

    // Determine how many days (or units) to show initially in the viewport
    get getVisibleDuration() {
        switch (this.viewMode) {
            case 'day': return 7; // Show 7 days
            case 'week': return 12 * 7; // Show 12 weeks
            case 'month': return 12 * 30; // Show 12 months (approx)
            case 'quarter': return 4 * 90; // Show 4 quarters (approx)
            default: return 7;
        }
    }

    // Calculate pixel position for a given date on the timeline
    // This is crucial for rendering task bars accurately.
    dateToPixel(date: any) {
        const startOfTimeline = this.timeColumns[0]?.date ? new Date(this.timeColumns[0].date) : new Date(this.visibleStartDate);
        const diffInDays = differenceInDays(new Date(date), startOfTimeline);
        const pixelsPerDay = this.getColumnWidth / (this.viewMode === 'day' ? 1 : this.viewMode === 'week' ? 7 : this.viewMode === 'month' ? 30 : 90); // Rough estimate
        return diffInDays * pixelsPerDay;
    }

    pixelToDate(pixel: any) {
        const startOfTimeline = this.timeColumns[0]?.date ? new Date(this.timeColumns[0].date) : new Date(this.visibleStartDate);
        const pixelsPerDay = this.getColumnWidth / (this.viewMode === 'day' ? 1 : this.viewMode === 'week' ? 7 : this.viewMode === 'month' ? 30 : 90);
        const days = pixel / pixelsPerDay;
        return addDays(startOfTimeline, days);
    }
}

export const ganttStore = new GanttStore();
